/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import afterRender from 'cio/utils/after-render';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import { KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY } from 'cio/utils/visual-workflow-builder';
import { storageFor } from 'ember-local-storage';
import { tracked } from '@glimmer/tracking';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import CampaignModel from 'cio/models/campaign';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { VWBContext } from 'cio/utils/visual-workflow-builder/context';

interface VisualWorkflowBuilderZoomControlsArgs {
  campaign: CampaignModel;
  graphElement: HTMLDivElement | null;
  isReadOnly?: boolean;
  showZoomHintPopupOnClick?: boolean;
  showShortcutHints?: boolean;
  hideZoomToFit?: boolean;
  startImageExport: () => unknown;
  rootElement: HTMLDivElement | null;
  canvasElement: HTMLDivElement | null;
  trackInViewport: boolean;
  graphSize: { width: number; height: number };
  tab: VWBTab;
  vwbContext: VWBContext;
  demoMode: boolean;
}

export default class VisualWorkflowBuilderZoomControls extends Component<VisualWorkflowBuilderZoomControlsArgs> {
  @service auth!: AuthService;

  @storageFor('cio-alerts') localStorage!: any;

  @tracked isMac = false;
  @tracked zoomInputElement: HTMLInputElement | null = null;
  @tracked zoomHintEmberBasicDropdown: BasicDropdown | null = null;
  @tracked allowClosingZoomHintPopup = false;

  get stickyNotesManager() {
    return this.args.vwbContext.stickyNotesManager;
  }

  get zoomManager() {
    let manager = this.args.vwbContext.zoomManager;
    return manager;
  }

  get stickyNoteCountText() {
    let count = this.stickyNotesManager.stickyNoteCount;

    if (count > 99) {
      return '99+';
    }

    return count;
  }

  get canExportImage() {
    // Exporting workflow images isn't available due to Content Security Policy
    // settings on our iframes
    return !this.args.demoMode;
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign.isApiTriggered === true) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  get canCreateOrEditNote() {
    return (
      !this.hasReadOnlyPermission &&
      this.stickyNotesManager.canCreateOrEditNote &&
      this.stickyNotesManager.isShowingStickyNotes
    );
  }

  shortcutTables = [
    [
      {
        label: 'Select multiple',
        shortcut: [{ name: 'shift', styled: true }],
      },
      {
        label: 'Copy',
        shortcut: [
          { name: 'option/alt', styled: true },
          { name: 'drag', styled: false },
        ],
      },
      {
        label: 'Delete',
        shortcut: [{ name: 'del/backspace', styled: true }],
      },
    ],

    [
      {
        label: 'Pan',
        shortcut: [
          // eslint-disable-next-line spellcheck/spell-checker
          { name: 'spacebar', styled: true },
          { name: 'drag', styled: false },
        ],
      },
      {
        // also pan
        label: '',
        shortcut: [
          // eslint-disable-next-line spellcheck/spell-checker
          { name: 'right-click', styled: false },
          { name: 'drag', styled: false },
        ],
      },
      {
        label: 'Zoom in/out',
        shortcut: [
          { name: 'command', styled: true },
          { name: '+/-', styled: true },
        ],
      },
    ],

    [
      {
        label: 'Show/Hide Notes',
        shortcut: [
          { name: 'shift', styled: true },
          { name: 'N', styled: true },
        ],
      },
    ],
  ];

  get roundedZoomLevel() {
    return `${Math.round(this.zoomManager.zoomLevel)}%`;
  }

  constructor(owner: unknown, args: VisualWorkflowBuilderZoomControlsArgs) {
    super(owner, args);

    let { platform } = navigator;
    this.isMac = (platform ?? '').includes('Mac');
  }

  get zoomKeyboardShortcutModifier() {
    // &#8984; is the Mac CMD clover
    return this.isMac ? '&#8984;' : 'Ctrl';
  }

  private updateZoomLevel(value: string) {
    let targetValue = parseInt(value);

    if (isNaN(targetValue)) {
      return;
    }

    // If the target value is negative, ignore it and reset the input value to
    // the current value
    if (this.zoomInputElement && targetValue <= 0) {
      return (this.zoomInputElement.value = this.roundedZoomLevel);
    }

    return this.zoomManager.zoomToValue(targetValue);
  }

  @action
  onKeyup(event: KeyboardEvent) {
    if (event.key !== 'Enter' || !event.target) {
      return;
    }
    let target = event.target as HTMLInputElement;

    this.updateZoomLevel(target.value);

    // Need to run this in runloop, because the _updateZoomLevel call will
    // change the input's value. We want to select the input's content after
    // that update has been performed.
    afterRender(() => {
      target.focus();
      target.select();
    });
  }

  @action
  onBlur(event: KeyboardEvent) {
    if (!event.target) {
      return;
    }

    let target = event.target as HTMLInputElement;
    return this.updateZoomLevel(target.value);
  }

  @action
  selectAllInInput(event: MouseEvent) {
    if (!event.target) {
      return;
    }

    let target = event.target as HTMLInputElement;
    target.focus();
    target.select();
  }

  @action
  onScrollShortcutDropdownClose() {
    return this.allowClosingZoomHintPopup;
  }

  @action
  showZoomHintPopupIfEnabled() {
    if (
      this.args.showZoomHintPopupOnClick &&
      this.zoomHintEmberBasicDropdown &&
      this.args.showShortcutHints !== false
    ) {
      this.zoomHintEmberBasicDropdown.actions.open();
    }
  }

  @action
  closeZoomHintPopup() {
    if (this.zoomHintEmberBasicDropdown) {
      this.allowClosingZoomHintPopup = true;
      this.zoomHintEmberBasicDropdown.actions.close();
      this.localStorage.set(KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY, true);
    }
  }

  @action
  registerZoomHintEmberBasicDropdown(dropdown: BasicDropdown) {
    this.zoomHintEmberBasicDropdown = dropdown;
  }

  @action
  registerZoomInput(element: HTMLInputElement) {
    this.zoomInputElement = element;
  }
}
